import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";
import { ArrowRight, Mail, Lock, User, Store } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

export default function Signup() {
  const { register, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const planParam = params.get("plan");
  const cycleParam = params.get("cycle") || "monthly";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cloverMerchantName, setCloverMerchantName] = useState(null);
  const [hasCloverToken, setHasCloverToken] = useState(false);

  useEffect(() => {
    if (user && typeof user === "object") nav("/dashboard");
  }, [user, nav]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const t = window.sessionStorage.getItem("pending_clover_token");
    if (t) {
      setHasCloverToken(true);
      setCloverMerchantName(window.sessionStorage.getItem("clover_merchant_name"));
    }
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    const r = await register(email, password, name);
    if (!r.ok) {
      setSubmitting(false);
      toast.error(r.error || "Sign up failed");
      return;
    }
    if (planParam) {
      // Send new user through Stripe Checkout for their selected plan.
      // Stripe's "Add promotion code" field handles all coupons natively
      // (allow_promotion_codes=True on session).
      try {
        const pkg = `${planParam}_${cycleParam}`;
        const { data } = await api.post("/billing/checkout", {
          package_id: pkg,
          origin_url: window.location.origin,
        });
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
      } catch (err) {
        toast.error(formatApiError(err));
      }
    }
    setSubmitting(false);
    toast.success("Account created!");
    nav("/dashboard");
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
      <Header />
      <div className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight">Create your account</h1>
            <p className="text-sm text-[#475569] mt-2">{planParam ? `Selected plan: ${planParam} (${cycleParam})` : "Start with the Starter plan — change anytime."}</p>
          </div>
          {hasCloverToken && (
            <div
              data-testid="clover-link-banner"
              className="mb-5 rounded-xl border border-[#F26953]/30 bg-[#FEE7E1]/60 px-4 py-3 flex items-start gap-3"
            >
              <div className="h-9 w-9 rounded-lg bg-[#F26953] grid place-items-center flex-shrink-0">
                <Store className="h-4 w-4 text-white" />
              </div>
              <div className="text-sm">
                <div className="font-semibold text-[#0F172A]">
                  {cloverMerchantName ? `Linking ${cloverMerchantName} on Clover` : "Clover detected"}
                </div>
                <div className="text-[#475569] mt-0.5">
                  Finish creating your oly Inbox account and we'll auto-connect your Clover POS.
                </div>
              </div>
            </div>
          )}
          <form onSubmit={onSubmit} className="bg-white rounded-2xl border border-[#E2E8F0] p-8 shadow-sm space-y-5" data-testid="signup-form">
            <label className="block">
              <span className="text-sm font-medium text-[#0F172A]">Your name</span>
              <div className="mt-1.5 relative">
                <User className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe"
                  data-testid="signup-name-input"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#F26953]/20 focus:border-[#F26953] outline-none text-sm"/>
              </div>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-[#0F172A]">Email</span>
              <div className="mt-1.5 relative">
                <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" data-testid="signup-email-input"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#F26953]/20 focus:border-[#F26953] outline-none text-sm"/>
              </div>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-[#0F172A]">Password</span>
              <div className="mt-1.5 relative">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="signup-password-input" placeholder="At least 8 characters"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#F26953]/20 focus:border-[#F26953] outline-none text-sm"/>
              </div>
            </label>
            <button type="submit" disabled={submitting} data-testid="signup-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#F26953] text-white font-semibold py-3 rounded-lg hover:bg-[#D85340] disabled:opacity-60 transition-colors">
              {submitting ? "Creating account…" : <>Create account <ArrowRight className="h-4 w-4"/></>}
            </button>
            <p data-testid="signup-newsletter-disclosure" className="text-[11px] leading-relaxed text-[#94A3B8] text-center">
              By creating an account, you'll also get our short weekly email with domain &amp; deliverability tips. One-click unsubscribe in every issue.
            </p>
            <p className="text-center text-sm text-[#475569]">
              Already have an account? <Link to="/login" data-testid="signup-to-login-link" className="text-[#F26953] font-semibold hover:underline">Sign in</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
